import React, { useEffect, useMemo, useState } from 'react'
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import PublishIcon from '@material-ui/icons/Publish'

import { useOrganizationCompanyList } from '../../hooks/useOrganizationCompanyList'
import { useUser } from '../../hooks/useUser'
import { PipelineBuilderTab } from '../PipelineBuilder'
import { usePipelineConfigTemplates } from '../PipelineBuilder'
import {
    PipelineConfigCreateFields,
    PipelineConfigStartMode,
} from '../LeasingSeason/Configurations/PipelineBuilder/PipelineConfigCreateFields'
import { formatOrgPipelineProvenance } from '../LeasingSeason/Configurations/PipelineBuilder/pipelineConfigProvenance'
import {
    useApplyOrgLeadsPipelineToProperties,
    useCreateOrgLeadsPipelineConfig,
    useOrgLeadsPipelineConfigs,
    extractOrgPipelineNameError,
} from './useOrgPipelineConfigApi'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        width: '100%',
    },
    headerRow: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1.5),
    },
    configSelect: {
        minWidth: 260,
    },
    loading: {
        display: 'flex',
        justifyContent: 'center',
        padding: theme.spacing(4),
    },
    buttonRow: {
        display: 'flex',
        gap: theme.spacing(1),
    },
    propertyList: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5),
        marginTop: theme.spacing(1),
        maxHeight: 280,
        overflowY: 'auto',
    },
    applyActions: {
        display: 'flex',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    empty: {
        color: theme.palette.text.secondary,
        fontSize: '0.875rem',
    },
}))

const DEFAULT_START_MODE: PipelineConfigStartMode = 'template'

function resetCreateDialogState(
    setNewConfigName: (value: string) => void,
    setStartMode: (value: PipelineConfigStartMode) => void,
    setTemplateKey: (value: string) => void,
) {
    setNewConfigName('')
    setStartMode(DEFAULT_START_MODE)
    setTemplateKey('')
}

export const EzFolioLeadsPipelineTab: React.FC = () => {
    const classes = useStyles()
    const { organization } = useUser()
    const organizationId = organization

    const apartmentsQuery = useOrganizationCompanyList(
        organizationId,
        'APARTMENT',
    )
    const apartments = apartmentsQuery.data ?? []

    const configsQuery = useOrgLeadsPipelineConfigs(organizationId)
    const templatesQuery = usePipelineConfigTemplates()
    const createMutation = useCreateOrgLeadsPipelineConfig(organizationId)
    const applyMutation = useApplyOrgLeadsPipelineToProperties(organizationId)

    const [selectedConfigId, setSelectedConfigId] = useState<number | ''>('')
    const [createOpen, setCreateOpen] = useState(false)
    const [applyOpen, setApplyOpen] = useState(false)
    const [newConfigName, setNewConfigName] = useState('')
    const [startMode, setStartMode] = useState<PipelineConfigStartMode>(
        DEFAULT_START_MODE,
    )
    const [templateKey, setTemplateKey] = useState('')
    const [createNameError, setCreateNameError] = useState<string | null>(null)
    const [selectedPropertyIds, setSelectedPropertyIds] = useState<number[]>([])

    const configs = configsQuery.data ?? []
    const templates = templatesQuery.data ?? []

    const templatesByKey = useMemo(
        () => new Map(templates.map((template) => [template.key, template])),
        [templates],
    )

    useEffect(() => {
        if (configs.length === 0) {
            setSelectedConfigId('')
            return
        }
        if (
            selectedConfigId === '' ||
            !configs.some((config) => config.id === selectedConfigId)
        ) {
            setSelectedConfigId(configs[0].id)
        }
    }, [configs, selectedConfigId])

    useEffect(() => {
        if (applyOpen) {
            setSelectedPropertyIds(apartments.map((apt) => apt.id))
        }
    }, [applyOpen, apartments])

    const closeCreateDialog = () => {
        setCreateOpen(false)
        setCreateNameError(null)
        resetCreateDialogState(setNewConfigName, setStartMode, setTemplateKey)
    }

    const formatConfigLabel = (config: typeof configs[number]) => {
        const provenance = formatOrgPipelineProvenance(config, templatesByKey)
        const suffix = provenance ? ` — ${provenance}` : ''
        return `${config.name}${suffix}`
    }

    const handleCreate = () => {
        const name = newConfigName.trim()
        if (!name) return
        if (startMode === 'template' && !templateKey) return

        const payload =
            startMode === 'template'
                ? { name, template_key: templateKey }
                : { name }

        createMutation.mutate(payload, {
            onSuccess: (data) => {
                closeCreateDialog()
                setSelectedConfigId(data.id)
            },
            onError: (error) => {
                setCreateNameError(
                    extractOrgPipelineNameError(error) ??
                        'Could not create org pipeline configuration.',
                )
            },
        })
    }

    const toggleProperty = (propertyId: number) => {
        setSelectedPropertyIds((prev) =>
            prev.includes(propertyId)
                ? prev.filter((id) => id !== propertyId)
                : [...prev, propertyId],
        )
    }

    const handleApply = () => {
        if (selectedConfigId === '' || selectedPropertyIds.length === 0) return
        applyMutation.mutate(
            {
                configId: selectedConfigId as number,
                property_ids: selectedPropertyIds,
            },
            { onSuccess: () => setApplyOpen(false) },
        )
    }

    if (organizationId == null) {
        return (
            <Typography color="textSecondary">
                Organization context is required to manage leads pipeline
                configs.
            </Typography>
        )
    }

    if (configsQuery.isLoading || templatesQuery.isLoading) {
        return (
            <Box className={classes.loading}>
                <CircularProgress size={28} />
            </Box>
        )
    }

    return (
        <Box className={classes.root}>
            <Box className={classes.headerRow}>
                <FormControl
                    variant="outlined"
                    size="small"
                    className={classes.configSelect}
                >
                    <InputLabel>Org pipeline</InputLabel>
                    <Select
                        value={selectedConfigId}
                        onChange={(e) =>
                            setSelectedConfigId(e.target.value as number)
                        }
                        label="Org pipeline"
                    >
                        {configs.map((config) => (
                            <MenuItem key={config.id} value={config.id}>
                                {formatConfigLabel(config)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Box className={classes.buttonRow}>
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => setCreateOpen(true)}
                    >
                        New pipeline
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<PublishIcon />}
                        disabled={
                            selectedConfigId === '' || apartments.length === 0
                        }
                        onClick={() => setApplyOpen(true)}
                    >
                        Apply to properties
                    </Button>
                </Box>
            </Box>

            {configs.length === 0 ? (
                <Typography className={classes.empty}>
                    No org pipeline configurations yet. Create one from an EZOS
                    template, then apply it to properties.
                </Typography>
            ) : (
                selectedConfigId !== '' && (
                    <PipelineBuilderTab
                        scope="org"
                        organizationId={organizationId}
                        configId={selectedConfigId}
                        fullWidth
                    />
                )
            )}

            <Dialog
                open={createOpen}
                onClose={closeCreateDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Create org pipeline</DialogTitle>
                <DialogContent>
                    <PipelineConfigCreateFields
                        name={newConfigName}
                        onNameChange={(value) => {
                            setCreateNameError(null)
                            setNewConfigName(value)
                        }}
                        startMode={startMode}
                        onStartModeChange={setStartMode}
                        templateKey={templateKey}
                        onTemplateKeyChange={setTemplateKey}
                        templates={templates}
                        templatesLoading={templatesQuery.isLoading}
                        namePlaceholder="e.g. Qualify First student housing"
                        nameError={createNameError}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeCreateDialog}>Cancel</Button>
                    <Button
                        color="primary"
                        variant="contained"
                        disabled={
                            !newConfigName.trim() ||
                            (startMode === 'template' && !templateKey) ||
                            createMutation.isLoading
                        }
                        onClick={handleCreate}
                    >
                        Create
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={applyOpen}
                onClose={() => setApplyOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Apply pipeline to properties</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" paragraph>
                        Creates independent property copies. Org edits will not
                        update properties already applied unless you re-apply to
                        editable copies.
                    </Typography>
                    <Box className={classes.applyActions}>
                        <Button
                            size="small"
                            onClick={() =>
                                setSelectedPropertyIds(
                                    apartments.map((apt) => apt.id),
                                )
                            }
                        >
                            Select all
                        </Button>
                        <Button
                            size="small"
                            onClick={() => setSelectedPropertyIds([])}
                        >
                            Clear all
                        </Button>
                    </Box>
                    <Box className={classes.propertyList}>
                        {apartments.map((apt) => (
                            <FormControlLabel
                                key={apt.id}
                                control={
                                    <Checkbox
                                        color="primary"
                                        checked={selectedPropertyIds.includes(
                                            apt.id,
                                        )}
                                        onChange={() => toggleProperty(apt.id)}
                                    />
                                }
                                label={apt.name}
                            />
                        ))}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setApplyOpen(false)}>Cancel</Button>
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={handleApply}
                        disabled={
                            selectedPropertyIds.length === 0 ||
                            applyMutation.isLoading
                        }
                    >
                        Apply
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}
